import React, { useState, useEffect, useRef } from 'react';
import { Input } from 'antd';
import * as translations from './strings';
import cx from 'classnames';
import styles from './styles.module.css';
import { getLangKey } from '../../../../models/Language';
import { get } from '../../../../../../redesign/javascript/utils/fetch';
import { Sparkles } from '../../../../../../redesign/javascript/components/Sparkles';
import { CircleX as Clear } from 'lucide-react';

const strings = translations[getLangKey()];
const { TextArea } = Input;

type Props = {
  title: string | null;
  canEditTitle: boolean;
  updateTitle: (
    title: string,
    aiSuggestedTitle: boolean,
    titleSuggestion: boolean
  ) => void;
  projectId: number;
  taskId: number;
  description: string;
  canUseAiSuggestedTitles?: boolean;
  isSidebar?: boolean;
  aiSuggestedTitle: boolean;
};

const Title = ({
  title,
  canEditTitle,
  updateTitle,
  projectId,
  taskId,
  description,
  canUseAiSuggestedTitles,
  isSidebar,
  aiSuggestedTitle
}: Props) => {
  const [tempTitle, setTempTitle] = useState<string>(title || '');
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [focused, setFocused] = useState<boolean>(false);
  const [cleared, setCleared] = useState<boolean>(false);
  const [hasClearedBefore, setHasClearedBefore] = useState<boolean>(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const taskIdAtBlurRef = useRef<number>(taskId);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    taskIdAtBlurRef.current = taskId;
    setTempTitle(title || '');
    setSuggestion(null);
    setFocused(false);
  }, [taskId]);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (cleared && !hasClearedBefore) setHasClearedBefore(true);
  }, [cleared]);

  const isDarkMode = document.body.classList.contains('dark-theme');

  const getGeneratedTitle = async () => {
    if (
      aiSuggestedTitle ||
      suggestion ||
      !canUseAiSuggestedTitles ||
      title ||
      !description ||
      hasClearedBefore
    ) {
      return;
    }

    try {
      const data = await get(
        `/projects/${projectId}/tasks/${taskId}/generate_title`
      );
      if (data.title) setSuggestion(data.title);
    } catch (error) {
      console.error('Failed to generate title:', error);
    }
  };

  useEffect(() => {
    if (focused) {
      inputRef.current?.focus();
    }
  }, [focused]);

  const blurInput = (element: HTMLTextAreaElement) => {
    element.blur();
    setFocused(false);
  };

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const element = event.currentTarget;
    switch (event.key) {
      case 'Escape':
        setTempTitle(title || '');
        return blurInput(element);
      case 'Tab':
        if (!suggestion) return blurInput(element);
        setTempTitle(suggestion);
        updateTitle(suggestion, false, true);
        setSuggestion(null);
        return;
      case 'Enter':
        updateTitle(tempTitle, false, !!tempTitle && suggestion === tempTitle);
        return blurInput(element);
    }
  };

  const handleOnBlur = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    const blurTaskId = taskIdAtBlurRef.current;
    blurTimeoutRef.current = setTimeout(() => {
      if (blurTaskId !== taskIdAtBlurRef.current) return;
      setFocused(false);
      updateTitle(tempTitle, false, !!tempTitle && suggestion === tempTitle);
    }, 500);
  };
  const showClearIcon = (focused || hovered) && !!tempTitle;

  return (
    <div className={styles.titleWrapper}>
      <span
        className={cx(styles.aiContainer, {
          [styles.showSuggested]:
            (aiSuggestedTitle && title) ||
            (focused && suggestion && (!tempTitle || tempTitle === suggestion))
        })}
      >
        <span className={styles.suggestionIndicator}>
          <Sparkles
            className={styles.sparklesIcon}
            linearGradient
            isDarkMode={isDarkMode}
            size={12}
          />
          <span>Suggestion</span>
        </span>
        {focused && (
          <span className={styles.tabToAccept}>
            <span className={styles.tabKey}>TAB</span>
            <span className={styles.tabAcceptText}> to accept</span>
          </span>
        )}
      </span>
      <div
        className={styles.titleField}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <TextArea
          ref={inputRef}
          className={cx(styles.titleInput, {
            [styles.sidebarTitle]: isSidebar,
            [styles.titleSuggestion]: suggestion,
            [styles.uneditable]: !canEditTitle,
            [styles.withSuffix]: !!tempTitle,
            [styles.suggestedTitle]: aiSuggestedTitle && title
          })}
          value={tempTitle}
          data-cypress-trigger="taskTitleInput"
          onChange={event => setTempTitle(event.target.value)}
          placeholder={(focused && suggestion) || strings.emptyTitle}
          onKeyDown={handleOnKeyDown}
          onBlur={handleOnBlur}
          readOnly={!canEditTitle}
          onFocus={() => {
            getGeneratedTitle();
            setFocused(true);
          }}
          autoSize={{ minRows: 1, maxRows: 8 }}
        />
        {showClearIcon && (
          <Clear
            size={13}
            className={cx(styles.clearIcon, styles.suffixIcon)}
            onClick={e => {
              e.preventDefault();
              setTempTitle('');
              setCleared(true);
              setSuggestion(null);
              updateTitle('', false, false);
              setCleared(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Title;
